import { useEffect, useRef } from 'react';

const ACCENT = '#FF7A45';
const INK = '#FFFFFF';
const MUTED = 'rgba(232,232,234,0.45)';
const RULE = '#26272A';

// A Klinik-Arztbrief — health data (DSGVO Art. 9), ICD-Codes, Versichertennummer.
// The kind of document that must never leave German soil.
const DOCUMENT: string[][] = [
  ['klinik', 'charité', 'campus', 'mitte', 'berlin'],
  ['arztbrief', 'innere', 'medizin', 'station', '24b'],
  ['patient', 'meier', 'johanna', 'geb.', '03.11.1958'],
  ['versichertennummer', 'aok', 'a123456789', 'gkv'],
  ['aufnahme', '14.05.2026', 'entlassung', '21.05.2026'],
  ['hauptdiagnose', 'i10.90', 'essentielle', 'hypertonie'],
  ['nebendiagnose', 'e11.9', 'diabetes', 'mellitus', 'typ', '2'],
  ['befund', 'blutdruck', '158/94', 'mmhg', 'in', 'ruhe'],
  ['labor', 'hba1c', '7,4', 'ldl', '142', 'mg/dl'],
  ['medikation', 'ramipril', '5', 'mg', 'morgens'],
  ['metformin', '850', 'mg', 'zweimal', 'täglich'],
  ['empfehlung', 'wiedervorstellung', 'in', '3', 'monaten'],
  ['unterzeichnet', 'dr.', 'med.', 'weber', 'oberarzt'],
  ['ärztliche', 'schweigepflicht', '§203', 'stgb'],
];

type Props = {
  cycleSeconds?: number;
};

export default function OcrMotion({ cycleSeconds = 10 }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // 14 lines, box widths proportional to actual word lengths, clamped within
    // the inner padding so nothing crosses the panel border.
    const USABLE_START = 0.04;
    const USABLE_END = 0.96;
    const GAP = 0.018;
    const LINES: Array<Array<{ x: number; w: number }>> = DOCUMENT.map((words) => {
      const totalChars = words.reduce((sum, w) => sum + w.length, 0) || 1;
      const gapTotal = Math.max(0, words.length - 1) * GAP;
      const wordBudget = USABLE_END - USABLE_START - gapTotal;
      let cursor = USABLE_START;
      return words.map((w) => {
        const ww = (w.length / totalChars) * wordBudget;
        const box = { x: cursor, w: ww };
        cursor += ww + GAP;
        return box;
      });
    });
    const wordAt = (line: number, idx: number) => DOCUMENT[line]?.[idx] ?? '';

    let widthCss = 0;
    let heightCss = 0;
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      widthCss = rect.width;
      heightCss = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(widthCss * dpr);
      canvas.height = Math.floor(heightCss * dpr);
      canvas.style.width = `${widthCss}px`;
      canvas.style.height = `${heightCss}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const start = performance.now();
    let rafId = 0;

    const draw = (now: number) => {
      const t = (now - start) / 1000;
      const W = widthCss;
      const H = heightCss;
      ctx.clearRect(0, 0, W, H);

      // Geometry mirrors motion-studies SCENE 6 exactly.
      const docX = 24;
      const docY = 24;
      const docW = W * 0.46;
      const docH = H - 48;
      const trX = W * 0.52;
      const trY = 24;
      const trW = W - trX - 24;
      const trH = H - 48;

      // doc background
      ctx.fillStyle = '#0F0F11';
      ctx.fillRect(docX, docY, docW, docH);
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(docX + 0.5, docY + 0.5, docW - 1, docH - 1);

      // doc "text" — raster word fills
      const lineGap = (docH - 32) / LINES.length;
      const lineH = lineGap * 0.55;
      LINES.forEach((words, li) => {
        const ly = docY + 16 + li * lineGap;
        words.forEach((word) => {
          const wx = docX + 16 + word.x * (docW - 32);
          const ww = word.w * (docW - 32);
          for (let r = 0; r < 3; r++) {
            ctx.fillStyle = `rgba(232,232,234,${0.18 + r * 0.06})`;
            ctx.fillRect(wx, ly + r * (lineH / 3), ww, Math.max(1, lineH / 3 - 1));
          }
        });
      });

      // 10s cycle: scan beam descends
      const cyc = reduced ? 1 : (t / cycleSeconds) % 1;
      const beamY = docY + 16 + cyc * (docH - 32);
      const linesRead = Math.floor(cyc * LINES.length);

      if (!reduced) {
        const beamGrad = ctx.createLinearGradient(0, beamY - 30, 0, beamY + 30);
        beamGrad.addColorStop(0, 'rgba(255,122,69,0)');
        beamGrad.addColorStop(0.5, 'rgba(255,122,69,0.35)');
        beamGrad.addColorStop(1, 'rgba(255,122,69,0)');
        ctx.fillStyle = beamGrad;
        ctx.fillRect(docX, beamY - 30, docW, 60);
        ctx.strokeStyle = ACCENT;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(docX, beamY);
        ctx.lineTo(docX + docW, beamY);
        ctx.stroke();
      }

      // bounding boxes on currently-scanning line
      const activeLine = Math.min(LINES.length - 1, linesRead);
      const activeRow = LINES[activeLine];
      if (activeRow) {
        const ly = docY + 16 + activeLine * lineGap;
        const localProg = cyc * LINES.length - linesRead;
        activeRow.forEach((word, wi) => {
          const wx = docX + 16 + word.x * (docW - 32);
          const ww = word.w * (docW - 32);
          const wordProg = wi / activeRow.length;
          if (localProg < wordProg) return;
          const settle = Math.min(1, (localProg - wordProg) * 8);
          ctx.strokeStyle = `rgba(255,122,69,${0.7 - settle * 0.3})`;
          ctx.lineWidth = 1;
          ctx.strokeRect(wx - 1, ly - 2, ww + 2, lineH + 4);
        });
      }
      // persistent faint boxes on already-read lines
      for (let li = 0; li < linesRead; li++) {
        const ly = docY + 16 + li * lineGap;
        const row = LINES[li];
        if (!row) continue;
        row.forEach((word) => {
          const wx = docX + 16 + word.x * (docW - 32);
          const ww = word.w * (docW - 32);
          ctx.strokeStyle = 'rgba(255,122,69,0.15)';
          ctx.lineWidth = 1;
          ctx.strokeRect(wx - 1, ly - 2, ww + 2, lineH + 4);
        });
      }

      // transcript panel
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fillRect(trX, trY, trW, trH);
      ctx.strokeStyle = RULE;
      ctx.strokeRect(trX + 0.5, trY + 0.5, trW - 1, trH - 1);

      ctx.font = '12px "JetBrains Mono", ui-monospace, monospace';
      const charH = 16;
      let ty = trY + 24;
      for (let li = 0; li <= Math.min(LINES.length - 1, linesRead); li++) {
        const row = LINES[li];
        if (!row) continue;
        const tx = trX + 16;
        let cx = tx;
        const localProg = li < linesRead ? 1 : cyc * LINES.length - linesRead;
        const wordsToShow = Math.min(row.length, Math.floor(localProg * row.length) + 1);

        ctx.fillStyle = 'rgba(232,232,234,0.3)';
        ctx.fillText(String(li + 1).padStart(2, '0'), tx, ty);
        cx = tx + 28;

        for (let wi = 0; wi < wordsToShow; wi++) {
          const word = wordAt(li, wi);
          const isCurrent = !reduced && li === linesRead && wi === wordsToShow - 1;
          const conf = 0.6 + ((li * 7 + wi * 11) % 40) / 100;
          ctx.fillStyle = isCurrent ? ACCENT : conf < 0.75 ? 'rgba(232,232,234,0.55)' : INK;
          ctx.fillText(word, cx, ty);
          const ww = ctx.measureText(word).width;
          if (conf < 0.75) {
            ctx.strokeStyle = 'rgba(255,122,69,0.4)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(cx, ty + 3);
            ctx.lineTo(cx + ww, ty + 3);
            ctx.stroke();
          }
          cx += ww + 6;
          if (cx > trX + trW - 60) break;
        }
        ty += charH;
        if (ty > trY + trH - 20) break;
      }

      // labels — match the original (offset 8px above each panel)
      ctx.fillStyle = MUTED;
      ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillText('SCAN', docX, docY - 8);
      ctx.fillText('TRANSCRIPT', trX, trY - 8);
      ctx.fillStyle = ACCENT;
      const lineLabel = `LINE ${Math.min(LINES.length, linesRead + 1)
        .toString()
        .padStart(2, '0')}/${LINES.length}`;
      const lw = ctx.measureText(lineLabel).width;
      ctx.fillText(lineLabel, trX + trW - lw, trY - 8);

      if (!reduced) {
        rafId = requestAnimationFrame(draw);
      }
    };

    if (reduced) {
      draw(start + cycleSeconds * 1000);
    } else {
      rafId = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [cycleSeconds]);

  return (
    <div ref={wrapRef} className="relative aspect-[16/7] w-full bg-black">
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
